import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Download, Calendar, Search } from 'lucide-react';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { SortButton } from '@/components/ui/SortButton.js';
import { Pagination } from '@/components/ui/Pagination.js';
import { Skeleton } from '@/components/ui/skeleton.js';

export function ReportsPage() {
  const { user } = useAuth();
  const role = user?.role || 'team_member';
  const isViewer = role === 'viewer';
  const isDevOrTeamMember = role === 'developer' || role === 'team_member';

  const [month, setMonth] = useState('2026-10');
  const [teamReport, setTeamReport] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<string>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState<number>(1);

  useEffect(() => {
    async function loadReports() {
      try {
        const res = await api.get<{ data: any }>(`/api/v1/reports/team?month=${month}`);
        if (res?.data) {
          const list = res.data.team_reports || (Array.isArray(res.data) ? res.data : []);
          setTeamReport(list);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    loadReports();
  }, [month]);

  const displayedReports = isDevOrTeamMember
    ? teamReport.filter((r) => (r.employee?.id || r.user_id) === user?.id)
    : teamReport;

  const handleExportCsv = (empId: string) => {
    window.open(`/api/v1/reports/employees/${empId}/export?month=${month}`, '_blank');
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = displayedReports.filter((r) => {
    const q = search.toLowerCase();
    const name = r.employee?.name || r.user_name || '';
    const empRole = r.employee?.role || r.role || '';
    return name.toLowerCase().includes(q) || empRole.toLowerCase().includes(q);
  });

  const sorted = [...filtered].sort((a: any, b: any) => {
    const aName = a.employee?.name || a.user_name || '';
    const bName = b.employee?.name || b.user_name || '';
    const aHours = Number(a.total_recorded_hours ?? a.recorded_hours ?? 0);
    const bHours = Number(b.total_recorded_hours ?? b.recorded_hours ?? 0);
    const aRate = Number(a.on_time_rate?.percentage ?? (a.on_time_completions ? (a.on_time_completions / Math.max(1, a.tasks_completed || 1)) * 100 : 0));
    const bRate = Number(b.on_time_rate?.percentage ?? (b.on_time_completions ? (b.on_time_completions / Math.max(1, b.tasks_completed || 1)) * 100 : 0));

    if (sortField === 'hours') {
      return sortDir === 'asc' ? aHours - bHours : bHours - aHours;
    }
    if (sortField === 'rate') {
      return sortDir === 'asc' ? aRate - bRate : bRate - aRate;
    }
    return sortDir === 'asc' ? aName.localeCompare(bName) : bName.localeCompare(aName);
  });

  const pageSize = 25;
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const validCurrentPage = Math.min(currentPage, totalPages);
  const paginated = sorted.slice((validCurrentPage - 1) * pageSize, validCurrentPage * pageSize);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Historical Reports</h1>
          <p className="helper-text mt-1">Asia/Karachi calendar bounded reporting, on-time completion rates, and audited hours.</p>
        </div>
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-slate-400" />
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-xs text-slate-700"
          />
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-3 rounded-lg border border-slate-200">
        <div className="relative flex-1 sm:w-64">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Search employees..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            className="pl-9 h-9 text-xs"
          />
        </div>
        <div className="flex items-center space-x-4 text-xs text-slate-500">
          <span className="font-medium text-slate-400">Sort by:</span>
          <SortButton label="Employee" field="name" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <SortButton label="Recorded Hours" field="hours" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <SortButton label="On-Time Rate" field="rate" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
        </div>
      </div>

      {/* Reports Table */}
      <Card>
        <CardHeader className="py-3 px-4 border-b border-slate-100 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-semibold text-slate-800">Employee Monthly Performance ({month})</CardTitle>
          <Badge variant="secondary">{sorted.length} {sorted.length === 1 ? 'employee' : 'employees'}</Badge>
        </CardHeader>
        <div className="divide-y divide-slate-100">
          {loading ? (
            <div className="p-4 space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-2">
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center space-x-2">
                      <Skeleton className="h-4 w-36" />
                      <Skeleton className="h-4 w-16 rounded-full" />
                    </div>
                    <Skeleton className="h-3 w-64" />
                  </div>
                  <div className="flex items-center space-x-6">
                    <div className="space-y-1 text-right">
                      <Skeleton className="h-2.5 w-24 ml-auto" />
                      <Skeleton className="h-4 w-12 ml-auto" />
                    </div>
                    <Skeleton className="h-8 w-16 rounded-md" />
                  </div>
                </div>
              ))}
            </div>
          ) : paginated.length === 0 ? (
            <div className="p-8 text-center text-slate-500 text-sm">No recorded data found for this query.</div>
          ) : (
            paginated.map((r) => {
              const empId = r.employee?.id || r.user_id;
              const empName = r.employee?.name || r.user_name;
              const empRole = r.employee?.role || r.role;
              return (
                <div key={empId} className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-slate-50/70 transition-colors">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold text-sm text-brand-navy">{empName}</span>
                      <Badge variant="secondary" className="capitalize text-[10px]">{empRole}</Badge>
                    </div>
                    <div className="text-xs text-slate-500">
                      Recorded: <strong className="text-slate-700">{r.total_recorded_hours ?? r.recorded_hours ?? 0} hrs</strong> (Approved: {r.approved_hours ?? 0} hrs • Manual: {r.manual_hours ?? 0} hrs)
                    </div>
                  </div>

                  <div className="flex items-center space-x-6">
                    {/* On-Time Rate KPI */}
                    <div className="text-right">
                      <div className="text-xs text-slate-400">On-Time Completion Rate</div>
                      <div className="text-sm font-bold text-brand-teal">
                        {r.on_time_rate?.display || (r.on_time_completions !== undefined ? `${Math.round((r.on_time_completions / Math.max(1, (r.tasks_completed || 1))) * 100)}%` : 'N/A')}
                      </div>
                    </div>

                    {/* CSV Export Button: Hidden for Viewers */}
                    {!isViewer && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleExportCsv(empId)}
                        className="flex items-center space-x-1 text-xs"
                        title="Export sanitized CSV report"
                      >
                        <Download className="h-3.5 w-3.5" />
                        <span>CSV</span>
                      </Button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <Pagination
          totalItems={sorted.length}
          currentPage={validCurrentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      </Card>
    </div>
  );
}
